import {
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Heading,
  Input,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMerchant } from "../../../../hooks/useMerchant";
import type { CreateCustomer, Customer } from "../../../../types/types";

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (customerData: CreateCustomer) => void;
  editCustomerData?: Customer;
};

const initialFormState = {
  name: "",
  documentNumber: "",
  rentalsMerchantId: "",
  email: "",
  phoneNumber: "",
  muted: false,
};

const AddCustomerModal = ({
  open,
  onClose,
  onSubmit,
  editCustomerData,
}: Props): JSX.Element => {
  const { merchant } = useMerchant();

  const [formData, setFormData] = useState<CreateCustomer>(initialFormState);

  const customerData: CreateCustomer = useMemo(
    () => ({
      rentalsMerchantId: merchant?.id ?? "",
      name: formData.name,
      documentNumber: formData.documentNumber,
      email: formData.email,
      phoneNumber:
        formData.phoneNumber === "" ? "" : `57${formData.phoneNumber}`,
    }),
    [merchant, formData]
  );
  useEffect(() => {
    if (editCustomerData) {
      setFormData({
        name: editCustomerData.name ?? "",
        documentNumber: editCustomerData.documentNumber,
        rentalsMerchantId: merchant?.id ?? "",
        email: editCustomerData.email ?? "",
        phoneNumber: editCustomerData.phoneNumber?.replace(/^57/, "") ?? "",
      });
    } else {
      setFormData(initialFormState);
    }
  }, [editCustomerData, open, merchant?.id]);

  const isFormValid = useCallback(() => {
    return (
      customerData.documentNumber &&
      (!customerData.email ||
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerData.email)) &&
      (!customerData.phoneNumber ||
        /^3[0-9]{9}$/.test(customerData.phoneNumber.replace(/^57/, "")))
    );
  }, [customerData]);

  const resetForm = () => {
    setFormData(initialFormState);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/\D/g, "");
    if (value.startsWith("57")) {
      value = value.slice(2);
    }
    value = value.slice(0, 10);
    setFormData({ ...formData, phoneNumber: value });
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      onSubmit(customerData);
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(e) => !e.open && onClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="md" maxWidth="400px">
            <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
              <Dialog.Title>
                <Heading>
                  {editCustomerData ? "Editar Cliente" : "Agregar Cliente"}
                </Heading>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton marginTop="1" onClick={onClose} />
            </Dialog.CloseTrigger>
            <Dialog.Body py={6}>
              <VStack gap={4} align="stretch">
                {editCustomerData ? (
                  <HStack justify="space-between" mb={2}>
                    <Text fontWeight={600} mb={2}>
                      Número de documento:
                    </Text>
                    <Text>{editCustomerData.documentNumber}</Text>
                  </HStack>
                ) : (
                  <Box>
                    <Text fontWeight={600} mb={2}>
                      Número de documento
                    </Text>
                    <Input
                      value={formData.documentNumber}
                      onChange={(e) => {
                        if (!editCustomerData) {
                          setFormData({
                            ...formData,
                            documentNumber: e.target.value,
                          });
                        }
                      }}
                      placeholder="0000000000"
                    />
                  </Box>
                )}
                <Box>
                  <Text fontWeight={600} mb={2}>
                    Nombre y apellido
                  </Text>
                  <Input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Juan Pérez"
                  />
                </Box>
                <Box>
                  <Text fontWeight={600} mb={2}>
                    Correo
                  </Text>
                  <Input
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    type="email"
                    placeholder="juan@correo.com"
                  />
                </Box>
                <Box>
                  <Text fontWeight={600} mb={2}>
                    Teléfono
                  </Text>
                  <HStack>
                    <Input value="+57" readOnly />
                    <Input
                      value={formData.phoneNumber}
                      onChange={handlePhoneNumberChange}
                      placeholder="000 000 00 00"
                      maxLength={10}
                    />
                  </HStack>
                </Box>
                <Box display={"flex"} gap={"1rem"} flexDirection={"row"}>
                  <Button variant="outline" onClick={onClose} flexGrow={1}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    flexGrow={1}
                    disabled={!isFormValid()}
                  >
                    {editCustomerData ? "Guardar Cambios" : "Agregar Cliente"}
                  </Button>
                </Box>
              </VStack>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default AddCustomerModal;
